import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { postCall } from "../../../Api/axios";
import { toast } from "react-toastify";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import BackNavigationButton from "../../Shared/BackNavigationButton";
import { IGM_CODES } from "../../../constants/IGMCodes";

const formControlStyles = {
  height: "35px !important",
  "& .MuiInputLabel-root": {
    fontSize: "12px !important",
    transform: "translate(14px, 8px) scale(1)",
    "&.Mui-focused": {
      transform: "translate(14px, -6px) scale(0.75)",
    },
    "&.MuiFormLabel-filled": {
      transform: "translate(14px, -6px) scale(0.75)",
    },
  },
  "& .MuiSelect-select": {
    fontSize: "12px !important",
    padding: "8px 14px",
    display: "flex",
    height: "35px !important",
    alignItems: "center",
  },
  "& .MuiInputBase-input": {
    fontSize: "12px !important",
    padding: "0px 10px",
    lineHeight: "normal",
    height: "35px",
    display: "flex",
    alignItems: "center",
  },
};

const issueFields = [
  {
    id: "subject",
    title: "Subject",
    placeholder: "Enter the subject",
    type: "input",
    required: true,
  },
  {
    id: "issueType",
    title: "Issue Type",
    options: [
      { key: "Issue", value: "ISSUE" },
      { key: "Grievance", value: "GRIEVANCE" },
      { key: "Dispute", value: "DISPUTE" },
    ],
    type: "select",
    required: true,
  },
  {
    id: "description",
    title: "Description",
    placeholder: "Describe your issue",
    type: "textarea",
    required: true,
  },
  {
    id: "category",
    title: "Category",
    options: [
      { key: "ORDER", value: "ORDER" },
      { key: "ITEM", value: "ITEM" },
      { key: "FULFILLMENT", value: "FULFILLMENT" },
      { key: "AGENT", value: "AGENT" },
      { key: "PAYMENT", value: "PAYMENT" },
    ],
    type: "select",
    required: true,
  },
];

const validationSchema = Yup.object().shape({
  subject: Yup.string().required("Subject is required"),
  issueType: Yup.string().required("Issue Type is required"),
  description: Yup.string().required("Description is required"),
  category: Yup.string().required("Category is required"),
  subCategory: Yup.string().required("Sub-category is required"),
});

const RaiseIssue = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [subCategories, setSubCategories] = useState([]);
  const [apiInProgress, setApiInProgress] = useState(false);

  const initialValues = {
    logisticsOrderId: params.id || "",
    subject: "",
    description: "",
    category: "",
    subCategory: "",
    issueType: "",
    images: [],
  };

  const handleCategoryChange = (category) => {
    if (category) {
      const filteredSubCategories = IGM_CODES.filter(
        (code) => code.Category === category
      );
      setSubCategories(filteredSubCategories);
    } else {
      setSubCategories([]);
    }
  };

  const onSubmitIssue = (values) => {
    const url = `/api/v2/dashboard/issue`;
    setApiInProgress(true);

    postCall(url, values)
      .then((resp) => {
        toast.success("Issue raised successfully");
        setApiInProgress(false);
        navigate(`/orders/${params.id}`);
      })
      .catch((error) => {
        setApiInProgress(false);
        toast.error(error.message);
      });
  };

  return (
    <div>
      <div className="container bg-slate-50 mx-auto my-8">
        <div>
          <div
            className="w-full bg-white px-4 py-4 rounded-md h-full scrollbar-hidden"
            style={{ minHeight: "95%", maxHeight: "100%", overflow: "auto" }}
          >
            <div className="m-auto w-10/12 md:w-3/4 h-max">
              <BackNavigationButton onClick={() => navigate(`/orders/${params.id}`)} />
              <p className="text-lg font-semibold mb-4">Raise an Issue</p>

              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmitIssue}
              >
                {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
                  <Form>
                    {issueFields.map((field) => {
                      if (field.type === "input" || field.type === "textarea") {
                        return (
                          <div key={field.id} style={{ marginBottom: "1.5rem" }}>
                            <TextField
                              name={field.id}
                              label={field.title}
                              placeholder={field.placeholder}
                              fullWidth
                              multiline={field.type === "textarea"}
                              rows={field.type === "textarea" ? 3 : 1}
                              value={values[field.id]}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              disabled={apiInProgress}
                              error={Boolean(touched[field.id] && errors[field.id])}
                              helperText={touched[field.id] && errors[field.id]}
                              sx={formControlStyles}
                            />
                          </div>
                        );
                      } else if (field.type === "select") {
                        return (
                          <div key={field.id} style={{ marginBottom: "1.5rem" }}>
                            <FormControl fullWidth sx={formControlStyles} disabled={apiInProgress}>
                              <InputLabel>{field.title}</InputLabel>
                              <Select
                                name={field.id}
                                label={field.title}
                                value={values[field.id]}
                                onChange={(e) => {
                                  handleChange(e);
                                  if (field.id === "category") {
                                    handleCategoryChange(e.target.value);
                                    setFieldValue("subCategory", ""); // Reset subcategory
                                  }
                                }}
                                onBlur={handleBlur}
                                error={Boolean(touched[field.id] && errors[field.id])}
                              >
                                {field.options.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.key}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            {touched[field.id] && errors[field.id] && (
                              <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
                                {errors[field.id]}
                              </div>
                            )}
                          </div>
                        );
                      }
                      return null;
                    })}

                    {/* Sub-category field */}
                    <div style={{ marginBottom: "1.5rem" }}>
                      <FormControl fullWidth sx={formControlStyles} disabled={apiInProgress}>
                        <InputLabel>Sub-category</InputLabel>
                        <Select
                          name="subCategory"
                          label="Sub-category"
                          value={values.subCategory}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(touched.subCategory && errors.subCategory)}
                        >
                          {subCategories.map((sub) => (
                            <MenuItem key={sub.SubCategory} value={sub.SubCategory}>
                              {sub.Name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {touched.subCategory && errors.subCategory && (
                        <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
                          {errors.subCategory}
                        </div>
                      )}
                    </div>

                    <div className="flex mt-6">
                      <Button
                        disabled={apiInProgress}
                        size="small"
                        variant="contained"
                        color="primary"
                        type="submit"
                        sx={{ backgroundColor: "#1890ff" }}
                      >
                        {apiInProgress ? <CircularProgress size={16} /> : "Submit Issue"}
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RaiseIssue;
